import { Grid } from "@mui/material";
import {
  FaCommentSlash,
  FaExclamationTriangle,
  FaInbox,
  FaThumbsDown,
  FaThumbsUp,
} from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, dark, primary, success, error, light } = colors;
const { borderWidth } = borders;

function EmptyState({
  icon = <FaInbox size={15} />,
  title = "No data available",
  description = "",
  compact = false,
}) {
  return (
    <VuiBox
      minHeight={compact ? "90px" : "150px"}
      px={2}
      py={compact ? 2 : 3}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      borderRadius="14px"
      sx={{
        background: rgba(black.main, 0.018),
        border: `${borderWidth[1]} dashed ${rgba(black.main, 0.12)}`,
      }}
    >
      <VuiBox
        width="34px"
        height="34px"
        borderRadius="10px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={1}
        sx={{
          background: rgba(primary.main, 0.08),
          color: primary.main,
          border: `${borderWidth[1]} solid ${rgba(primary.main, 0.14)}`,
        }}
      >
        {icon}
      </VuiBox>

      <VuiTypography variant="button" color="dark" fontWeight="bold">
        {title}
      </VuiTypography>

      {description && (
        <VuiTypography
          variant="caption"
          color="dark"
          fontWeight="regular"
          sx={{
            mt: 0.4,
            opacity: 0.62,
            lineHeight: 1.5,
            maxWidth: "280px",
          }}
        >
          {description}
        </VuiTypography>
      )}
    </VuiBox>
  );
}

function SentimentCircle({ goodPercentage = 0, hasResponses = false }) {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;

  const safeGoodPercentage = Math.min(100, Math.max(0, Number(goodPercentage) || 0));

  const goodOffset = circumference - (safeGoodPercentage / 100) * circumference;

  return (
    <VuiBox
      width="160px"
      height="160px"
      minWidth="160px"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <svg width="160" height="160" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke={hasResponses ? rgba(error.main, 0.22) : rgba(black.main, 0.08)}
          strokeWidth="12"
        />

        {hasResponses && (
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={success.main}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={goodOffset}
            transform="rotate(-90 60 60)"
          />
        )}
      </svg>

      <VuiBox
        position="absolute"
        textAlign="center"
        width="90px"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <VuiTypography
          color="dark"
          fontWeight="bold"
          lineHeight={1}
          sx={{
            fontSize: "24px",
            whiteSpace: "nowrap",
            opacity: hasResponses ? 1 : 0.45,
          }}
        >
          {hasResponses ? `${safeGoodPercentage}%` : "—"}
        </VuiTypography>

        <VuiTypography
          variant="caption"
          color="dark"
          fontWeight="medium"
          sx={{
            mt: 1,
            opacity: 0.7,
          }}
        >
          {hasResponses ? "Good" : "No responses"}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
}

function ReasonList({ title, icon, items = [], type = "good" }) {
  const isGood = type === "good";
  const color = isGood ? success.main : error.main;
  const hasItems = items.length > 0;

  return (
    <VuiBox
      p={1.6}
      borderRadius="16px"
      sx={{
        background: light.main,
        border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
        height: "100%",
      }}
    >
      <VuiBox display="flex" alignItems="center" gap={1} mb={1.3}>
        <VuiBox
          width="26px"
          height="26px"
          borderRadius="9px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            background: rgba(color, 0.1),
            color,
            border: `${borderWidth[1]} solid ${rgba(color, 0.18)}`,
          }}
        >
          {icon}
        </VuiBox>

        <VuiTypography variant="button" color="dark" fontWeight="bold">
          {title}
        </VuiTypography>
      </VuiBox>

      {hasItems ? (
        <VuiBox display="flex" flexDirection="column" gap={1}>
          {items.map((item, index) => (
            <VuiBox
              key={`${item.label}-${index}`}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1.5}
            >
              <VuiTypography
                variant="caption"
                color="dark"
                fontWeight="medium"
                sx={{ opacity: 0.8 }}
              >
                {item.label}
              </VuiTypography>

              <VuiTypography variant="caption" color="dark" fontWeight="bold" whiteSpace="nowrap">
                {item.count || 0}
              </VuiTypography>
            </VuiBox>
          ))}
        </VuiBox>
      ) : (
        <EmptyState
          compact
          icon={isGood ? <FaThumbsUp size={13} /> : <FaThumbsDown size={13} />}
          title={isGood ? "No positive topics yet" : "No complaint topics yet"}
          description={
            isGood
              ? "Positive themes will appear as guests submit written feedback."
              : "Complaint themes will appear when negative feedback is received."
          }
        />
      )}
    </VuiBox>
  );
}

function TextFeedbackSentimentPanel({ summary }) {
  if (!summary) {
    return null;
  }

  const totalTextResponses = Number(summary?.totalTextResponses || 0);
  const goodCount = Number(summary?.sentiment?.good || 0);
  const poorCount = Number(summary?.sentiment?.poor || 0);

  const goodPercentage = Number(summary?.sentiment?.goodPercentage || 0);

  const poorPercentage = Number(summary?.sentiment?.poorPercentage || 0);

  const positiveReasons = summary?.topGoodReasons || [];
  const complaintReasons = summary?.topPoorReasons || [];
  const recentPoorResponses = summary?.recentPoorResponses || [];

  const hasResponses = totalTextResponses > 0;

  if (!hasResponses) {
    return (
      <VuiBox
        p={2}
        borderRadius="20px"
        sx={{
          background: light.main,
          border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
          boxShadow: "none",
        }}
      >
        <VuiBox mb={2}>
          <VuiTypography variant="h6" color="dark" fontWeight="bold">
            Guest Comment Health
          </VuiTypography>

          <VuiTypography
            variant="caption"
            color="dark"
            fontWeight="regular"
            sx={{
              opacity: 0.7,
              lineHeight: 1.5,
            }}
          >
            Good vs poor sentiment based on written guest responses.
          </VuiTypography>
        </VuiBox>

        <EmptyState
          icon={<FaCommentSlash size={16} />}
          title="No feedback yet"
          description="Sentiment analysis, common topics, and recent comments will appear after guests submit text responses."
        />
      </VuiBox>
    );
  }

  return (
    <VuiBox
      p={2}
      borderRadius="20px"
      sx={{
        background: light.main,
        border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
        boxShadow: "none",
      }}
    >
      <VuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <VuiBox>
          <VuiTypography variant="h6" color="dark" fontWeight="bold">
            Guest Comment Health
          </VuiTypography>

          <VuiTypography
            variant="caption"
            color="dark"
            fontWeight="regular"
            sx={{
              opacity: 0.7,
              lineHeight: 1.5,
            }}
          >
            Good vs poor sentiment based on written guest responses.
          </VuiTypography>
        </VuiBox>

        <VuiBox textAlign="right">
          <VuiTypography variant="h5" color="primary" fontWeight="bold">
            {totalTextResponses}
          </VuiTypography>

          <VuiTypography variant="caption" color="dark" fontWeight="medium" sx={{ opacity: 0.7 }}>
            text responses
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <VuiBox
            height="100%"
            p={2}
            borderRadius="18px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              background: light.secondary,
              border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
            }}
          >
            <SentimentCircle goodPercentage={goodPercentage} hasResponses={hasResponses} />

            <VuiBox mt={1.5} display="flex" gap={2.5} justifyContent="center" flexWrap="wrap">
              <VuiBox display="flex" alignItems="center" gap={0.7}>
                <VuiBox
                  width="9px"
                  height="9px"
                  borderRadius="50%"
                  sx={{ background: success.main }}
                />

                <VuiTypography variant="caption" color="dark" fontWeight="bold">
                  Good {goodPercentage}%
                </VuiTypography>
              </VuiBox>

              <VuiBox display="flex" alignItems="center" gap={0.7}>
                <VuiBox
                  width="9px"
                  height="9px"
                  borderRadius="50%"
                  sx={{
                    background: rgba(error.main, 0.45),
                  }}
                />

                <VuiTypography variant="caption" color="dark" fontWeight="bold">
                  Poor {poorPercentage}%
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            <VuiBox mt={1.4} display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <VuiTypography variant="caption" color="success" fontWeight="bold">
                {goodCount} good
              </VuiTypography>

              <VuiTypography variant="caption" color="error" fontWeight="bold">
                {poorCount} poor
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2} height="100%">
            <Grid item xs={12} md={6}>
              <ReasonList
                title="Top Positive Responses"
                icon={<FaThumbsUp size={12} />}
                items={positiveReasons}
                type="good"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ReasonList
                title="Top Complaint Responses"
                icon={<FaThumbsDown size={12} />}
                items={complaintReasons}
                type="poor"
              />
            </Grid>

            <Grid item xs={12}>
              <VuiBox
                p={1.6}
                borderRadius="16px"
                sx={{
                  background: light.main,
                  border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
                }}
              >
                <VuiBox display="flex" alignItems="center" gap={1} mb={1.2}>
                  <VuiBox
                    width="26px"
                    height="26px"
                    borderRadius="9px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      background: rgba(error.main, 0.1),
                      color: error.main,
                      border: `${borderWidth[1]} solid ${rgba(error.main, 0.18)}`,
                    }}
                  >
                    <FaExclamationTriangle size={12} />
                  </VuiBox>

                  <VuiTypography variant="button" color="dark" fontWeight="bold">
                    Recent Poor Feedback
                  </VuiTypography>
                </VuiBox>

                {recentPoorResponses.length > 0 ? (
                  <VuiBox display="flex" flexDirection="column" gap={0.8}>
                    {recentPoorResponses.map((response, index) => (
                      <VuiTypography
                        key={`${response}-${index}`}
                        variant="caption"
                        color="dark"
                        fontWeight="regular"
                        sx={{
                          opacity: 0.82,
                          lineHeight: 1.6,
                        }}
                      >
                        “{response}”
                      </VuiTypography>
                    ))}
                  </VuiBox>
                ) : (
                  <EmptyState
                    compact
                    icon={<FaExclamationTriangle size={13} />}
                    title="No recent poor feedback"
                    description="Recent negative guest comments will appear here."
                  />
                )}
              </VuiBox>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </VuiBox>
  );
}

export default TextFeedbackSentimentPanel;
